import React, {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import styled from '@emotion/styled'
import {theme} from '../../../../../accountManagement/theme'
import {Button} from '../../../../../core/shared/Button'
import {AddToOrderRow} from '../ProductPlanSelector.styles'

const amountToQuantityMap = {
  500: 454,
  600: 545,
  850: 772,
  1000: 909,
  1500: 1363,
  2000: 1818,
  2500: 2272,
  3000: 2727,
  4000: 3636,
  5000: 4545,
  6000: 5454,
  7000: 6363,
  8000: 7272,
  9000: 8181,
  10000: 9090,
}

// Only the regular Leads Lift SKU gets a tier picker — Leads Lift BNI is a
// usage-based product with no picker (see AddonsGrid/DirectStepperCard's
// plain "Add to Order" card + the BNI requires-MC gate in
// productSelectionRules.js's getDisabledPlanCodes).
export const LEADS_LIFT_PLAN_CODE = 'addon-5-leadslift-m2m'

export function isLeadsLiftPlan(planCode) {
  return planCode === LEADS_LIFT_PLAN_CODE
}

// Leads Lift is billed at one of 15 fixed dollar tiers rather than a normal
// per-unit price — the "quantity" stored on the line item is an internal
// Recurly accounting number (~amount / 1.1), not a count of anything the
// customer picks directly. amountToQuantityMap's keys are the tiers.
const TIER_AMOUNTS = Object.keys(amountToQuantityMap)
  .map(Number)
  .sort((a, b) => a - b)

const TIER_CATEGORY_BY_AMOUNT = {
  500: 'Express',
  600: 'Express',
  850: 'Standard',
  1000: 'Standard',
  1500: 'Standard',
  2000: 'Premium',
  2500: 'Premium',
  3000: 'Premium',
  4000: 'Premium',
  5000: 'Premium',
  6000: 'Premium',
  7000: 'Premium',
  8000: 'Premium',
  9000: 'Premium',
  10000: 'Premium',
}

// Deliberately a plain "$X" amount, not locale-aware currencyFormat —
// matches the legacy Leads Lift pricing display, which is intentionally
// kept as-is (unlike every other product) per product decision.
const TIER_OPTIONS = TIER_AMOUNTS.map(tier => ({
  tier,
  label: `${TIER_CATEGORY_BY_AMOUNT[tier]} - $${tier.toLocaleString()}`,
}))

// Reverse-maps a stored Recurly quantity back to its canonical dollar tier
// (closest match) — mirrors the legacy planAmountNum/formatPrice logic,
// since Recurly can store a slightly-off quantity for an existing
// subscription.
function tierForQuantity(quantity) {
  if (!quantity) return null
  return TIER_AMOUNTS.reduce((closest, tier) =>
    Math.abs(amountToQuantityMap[tier] - quantity) <
    Math.abs(amountToQuantityMap[closest] - quantity)
      ? tier
      : closest,
  )
}

// "Current"/"Upgrade"/"Downgrade" tags only make sense relative to an
// existing tier — a brand-new add just shows the plain search list.
function tagForTier(tier, currentTier) {
  if (currentTier == null) return null
  if (tier === currentTier) return 'Current'
  return tier > currentTier ? 'Upgrade' : 'Downgrade'
}

function highlightMatch(label, query) {
  const trimmed = query.trim()
  if (!trimmed) return label
  const index = label.toLowerCase().indexOf(trimmed.toLowerCase())
  if (index === -1) return label
  return (
    <>
      {label.slice(0, index)}
      <Highlight>{label.slice(index, index + trimmed.length)}</Highlight>
      {label.slice(index + trimmed.length)}
    </>
  )
}

const PanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[4]};
`

const ComboboxWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  max-width: 320px;
`

const ComboboxLabel = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
`

const ComboboxInput = styled.input`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  padding: ${theme.space[2]} ${theme.space[2.5]};
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.border.default};
  outline: none;

  &:focus {
    border-color: ${theme.colors.action.primary};
  }
`

// Positioned via inline style (fixed, computed from the input's bounding
// rect) rather than absolute — this is rendered through a portal into
// document.body so it can escape PanelWrapper's overflow: hidden, which
// otherwise clips it (that overflow is load-bearing for PanelWrapper's
// rounded-corner shadow elsewhere, so it isn't something to change there).
const ComboboxList = styled.div`
  position: fixed;
  z-index: ${theme.zIndex.dropdown};
  background: ${theme.colors.bg.primary};
  border-radius: ${theme.radii.md};
  box-shadow: 0 4px 12px 0 #00000021;
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const ComboboxOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space[2]};
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.space[2]} ${theme.space[2.5]};
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  text-align: left;

  &:hover {
    background: ${theme.colors.bg.brand};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background: none;
    }
  }
`

const ComboboxTag = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.muted};
  flex-shrink: 0;
`

const Highlight = styled.span`
  background: #ffe8b3;
  border-radius: 2px;
`

const ErrorText = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.status.error};
`

const SelectedText = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
`

const Tip = styled.span`
  font-family: ${theme.fonts.openSans};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.muted};
`

// Owns the Leads Lift tier picker: renders inside ProductPlanSelector's
// right panel in place of the generic fields UI. A searchable combobox
// rather than a plain dropdown, mirroring the legacy SearchDropdown —
// typing filters the 15 tiers by label, and an already-in-order item shows
// each option's relation (Current/Upgrade/Downgrade) to its existing tier;
// a brand-new add shows the plain filtered list with no tags.
//
// Selecting a tier and submitting builds a line item whose `quantity` is
// the Recurly-facing accounting number (amountToQuantityMap[tier]) and
// whose `price` is the tier dollar amount itself — order-summary totals
// elsewhere already sum `price`/`unitPrice` directly without multiplying
// by quantity, so this is consistent with how every other addon's line
// item is built.
export function LeadsLiftPanel({
  plan,
  itemInOrder,
  groupPlanCodes,
  groupId,
  onAddProduct,
  onUpdateProduct,
  onClose,
}) {
  const isItemInOrder = Boolean(itemInOrder)
  const currentTier = isItemInOrder
    ? tierForQuantity(itemInOrder.quantity)
    : null
  const currentOption = TIER_OPTIONS.find(option => option.tier === currentTier)

  const [selectedTier, setSelectedTier] = useState(currentTier ?? null)
  const [searchText, setSearchText] = useState(currentOption?.label ?? '')
  const [isOpen, setIsOpen] = useState(false)
  const [touched, setTouched] = useState(false)
  const [dropdownRect, setDropdownRect] = useState(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const filteredOptions = TIER_OPTIONS.filter(option =>
    option.label.toLowerCase().includes(searchText.trim().toLowerCase()),
  )

  const openDropdown = () => {
    const rect = inputRef.current?.getBoundingClientRect()
    if (rect) {
      setDropdownRect({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      })
    }
    setIsOpen(true)
  }

  // The dropdown is portaled to document.body with a fixed position
  // computed above, so it doesn't track the input if the page scrolls
  // while open — simplest to just close it rather than re-measure on
  // every scroll event. Scrolling *inside* the list itself (it has its own
  // overflow-y: auto for the 15 options) also fires a capture-phase scroll
  // event, so that has to be excluded or the list would close on its own
  // scrollbar.
  useEffect(() => {
    if (!isOpen) return undefined
    const closeOnScroll = e => {
      if (listRef.current?.contains(e.target)) return
      setIsOpen(false)
    }
    window.addEventListener('scroll', closeOnScroll, true)
    return () => window.removeEventListener('scroll', closeOnScroll, true)
  }, [isOpen])

  const handleSelect = option => {
    setSelectedTier(option.tier)
    setSearchText(option.label)
    setIsOpen(false)
    setTouched(false)
  }

  const handleAddToOrder = () => {
    if (!selectedTier) {
      setTouched(true)
      return
    }

    const product = {
      productId: plan.code,
      name: plan.name,
      price: selectedTier,
      type: 'addon',
      billingFrequency:
        plan.productBillingType === 'Annual' ? 'annual' : 'monthly',
      quantity: amountToQuantityMap[selectedTier],
      // configuredOptions: {tier: selectedTier},
      excludes: plan.excludes ?? [],
      autoAddCenters: plan.autoAddCenters ?? [],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      parentProduct: plan.parentProduct,
    }

    if (isItemInOrder) {
      // Falls back to onAddProduct if the host screen hasn't wired up
      // onUpdateProduct — best-effort rather than a hard crash.
      ;(onUpdateProduct ?? (p => onAddProduct(p, groupPlanCodes, groupId)))(
        product,
      )
    } else {
      onAddProduct(product, groupPlanCodes, groupId)
    }
    onClose()
  }

  return (
    <PanelWrapper>
      <ComboboxWrapper>
        <ComboboxLabel>
          {isItemInOrder ? 'Change Tier' : 'Search Tier'}
        </ComboboxLabel>
        <ComboboxInput
          ref={inputRef}
          type="text"
          placeholder={
            currentTier
              ? `Search Tiers > $${currentTier.toLocaleString()}...`
              : 'Search Tiers'
          }
          value={searchText}
          onFocus={openDropdown}
          onBlur={() => setIsOpen(false)}
          onChange={e => {
            setSearchText(e.target.value)
            setSelectedTier(null)
            openDropdown()
          }}
        />
        {isOpen &&
          filteredOptions.length > 0 &&
          dropdownRect &&
          createPortal(
            <ComboboxList
              ref={listRef}
              style={{
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
              }}
            >
              {filteredOptions.map(option => {
                const tag = isItemInOrder
                  ? tagForTier(option.tier, currentTier)
                  : null
                const isCurrent = tag === 'Current'
                // Once the current tier is an account-owned (alreadyIncluded)
                // upgrade item, downgrading to a lower tier isn't allowed.
                const isBlockedDowngrade =
                  itemInOrder?.alreadyIncluded && tag === 'Downgrade'
                return (
                  <ComboboxOption
                    key={option.tier}
                    type="button"
                    disabled={isCurrent || isBlockedDowngrade}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSelect(option)}
                  >
                    <span>{highlightMatch(option.label, searchText)}</span>
                    {tag && <ComboboxTag>{tag}</ComboboxTag>}
                  </ComboboxOption>
                )
              })}
            </ComboboxList>,
            document.body,
          )}
        {selectedTier != null && (
          <SelectedText>Selected: {selectedTier.toLocaleString()}</SelectedText>
        )}
        {touched && !selectedTier && (
          <ErrorText>Please select a tier</ErrorText>
        )}
        <Tip>
          Tip: try typing &ldquo;st&rdquo;, &ldquo;ex&rdquo;, &ldquo;50&rdquo;,
          etc.
        </Tip>
      </ComboboxWrapper>

      <AddToOrderRow>
        <Button
          type="button"
          variant="action-orange"
          size="sm"
          onClick={handleAddToOrder}
        >
          {isItemInOrder ? 'Change Tier' : 'Add To Order'}
        </Button>
      </AddToOrderRow>
    </PanelWrapper>
  )
}
